import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { formatDate } from '../../lib/utils';

export const AddExpenseDialog = ({ open, onOpenChange, form, setForm, onSubmit, getCashFlowMonths }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>Add Cash Flow Expense</DialogTitle></DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Expense Head</label>
          <Input value={form.expense_head} onChange={(e) => setForm({ ...form, expense_head: e.target.value })} placeholder="e.g., Salaries, Rent" required />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Sub-Head (Optional)</label>
          <Input value={form.sub_head} onChange={(e) => setForm({ ...form, sub_head: e.target.value })} placeholder="e.g., Marketing Team" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              {form.is_recurring ? 'Starting Month' : 'Month'}
            </label>
            <Select value={form.month_year || '__none__'} onValueChange={(v) => setForm({ ...form, month_year: v === '__none__' ? '' : v })} required>
              <SelectTrigger><SelectValue placeholder="Select month" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- Select --</SelectItem>
                {[...getCashFlowMonths, ...Array.from({ length: 6 }, (_, i) => {
                  const d = new Date();
                  d.setMonth(d.getMonth() + 6 + i);
                  return { key: d.toISOString().slice(0, 7), name: d.toLocaleString('default', { month: 'short', year: 'numeric' }) };
                })].map(m => (
                  <SelectItem key={m.key} value={m.key}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Amount per month (INR)</label>
            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
          </div>
        </div>

        <div className="border rounded-lg p-3 bg-muted/30 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={Boolean(form.is_recurring)}
              onCheckedChange={(v) => setForm({ ...form, is_recurring: Boolean(v), recurring_months: v ? (form.recurring_months || 12) : 1 })}
              data-testid="add-expense-recurring-toggle"
            />
            <span className="text-sm font-medium">Recurring expense</span>
          </label>
          {form.is_recurring && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Repeat for</label>
              <Input
                type="number"
                min={2}
                max={60}
                className="w-24"
                value={form.recurring_months || 12}
                onChange={(e) => setForm({ ...form, recurring_months: Math.min(60, Math.max(2, parseInt(e.target.value, 10) || 2)) })}
                data-testid="add-expense-recurring-months"
              />
              <span className="text-sm text-muted-foreground">months (max 60)</span>
            </div>
          )}
          {form.is_recurring && (
            <p className="text-xs text-muted-foreground">
              This will create {form.recurring_months || 12} identical expenses starting from the selected month.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" data-testid="add-expense-submit">Add Expense</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const EditExpenseDialog = ({ open, onOpenChange, form, setForm, onSubmit, getCashFlowMonths }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="edit-expense-dialog">
      <DialogHeader><DialogTitle>Edit Cash Flow Expense</DialogTitle></DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Expense Head</label>
          <Input
            value={form.expense_head}
            onChange={(e) => setForm({ ...form, expense_head: e.target.value })}
            required
            data-testid="edit-expense-head"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Sub-Head (Optional)</label>
          <Input
            value={form.sub_head}
            onChange={(e) => setForm({ ...form, sub_head: e.target.value })}
            data-testid="edit-expense-subhead"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Month</label>
            <Select value={form.month_year || '__none__'} onValueChange={(v) => setForm({ ...form, month_year: v === '__none__' ? '' : v })} required>
              <SelectTrigger data-testid="edit-expense-month"><SelectValue placeholder="Select month" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- Select --</SelectItem>
                {[...getCashFlowMonths, ...Array.from({ length: 6 }, (_, i) => {
                  const d = new Date();
                  d.setMonth(d.getMonth() + 6 + i);
                  return { key: d.toISOString().slice(0, 7), name: d.toLocaleString('default', { month: 'short', year: 'numeric' }) };
                })].map(m => (
                  <SelectItem key={m.key} value={m.key}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Amount (INR)</label>
            <Input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
              data-testid="edit-expense-amount"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" data-testid="edit-expense-submit">Save Changes</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);


export const DrillDownDialog = ({ drillDown, setDrillDown, drillDownData, formatCurrency }) => {
  const isExcluded = (status) => status === 'Voided' || status === 'Cancelled';
  const getStatusBadgeClass = (status) => {
    if (status === 'Voided') return 'bg-red-500/20 text-red-600';
    if (status === 'Cancelled') return 'bg-orange-500/20 text-orange-600';
    if (status === 'Received') return 'bg-green-500/20 text-green-600';
    if (status === 'Partial') return 'bg-blue-500/20 text-blue-600';
    if (status === 'Invoiced') return 'bg-purple-500/20 text-purple-600';
    if (status === 'Pending') return 'bg-amber-500/20 text-amber-600';
    return 'bg-gray-500/15 text-gray-600';
  };
  const excludedCount = (drillDownData.items || []).filter((i) => isExcluded(i.status)).length;
  return (
  <Dialog open={!!drillDown} onOpenChange={(open) => !open && setDrillDown(null)}>
    <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          Cash Flow Detail - {drillDown?.monthName}
          <span className={`px-2 py-0.5 text-xs rounded-full ${
            drillDown?.type === 'income' ? 'bg-green-500/20 text-green-600' :
            drillDown?.type === 'expense' ? 'bg-red-500/20 text-red-600' :
            'bg-blue-500/20 text-blue-600'
          }`}>
            {drillDown?.type === 'income' ? 'Income' : drillDown?.type === 'expense' ? 'Expenses' : 'Net'}
          </span>
        </DialogTitle>
      </DialogHeader>
      {drillDownData.items.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No items for this period</p>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drillDownData.items.map((item, idx) => (
                <TableRow key={item.id || idx} data-testid={`drilldown-row-${item.id || idx}`}>
                  <TableCell>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      item.source === 'Fee Structure' ? 'bg-green-500/20 text-green-600' :
                      item.source === 'Cash Flow Expense' ? 'bg-red-500/20 text-red-600' :
                      'bg-amber-500/20 text-amber-600'
                    }`}>
                      {item.category || item.source}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{item.project}</TableCell>
                  <TableCell className="text-sm max-w-48 truncate">{item.description}</TableCell>
                  <TableCell className="text-sm">{item.date ? formatDate(item.date) : '-'}</TableCell>
                  <TableCell className={`text-right font-semibold ${(item.amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(Math.abs(item.amount || 0))}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadgeClass(item.status)}`}>
                      {item.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-between items-center pt-4 border-t mt-4">
            <span className="text-sm text-muted-foreground">
              {drillDownData.items.length} items
              {excludedCount > 0 && (
                <span className="ml-2 text-muted-foreground" data-testid="drilldown-excluded-count">
                  · {excludedCount} voided/cancelled (not in total)
                </span>
              )}
            </span>
            <span className={`text-lg font-bold ${drillDownData.total >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="drilldown-total">
              Total: {formatCurrency(Math.abs(drillDownData.total))}
              {drillDownData.total < 0 && ' (deficit)'}
            </span>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
  );
};
